/**
 * The companies the entity owns and the states it is registered in.
 *
 * Expects the subsidiaries and the state registrations the api sent, one TaxProfileListRow each,
 * the tax year the page shows, the "Needs review only" filter, the page's writes, busy while a
 * write is in flight, and the callback that opens a source document.
 *
 * Renders the section heading and, for each list that has rows, a ListTable: Subsidiaries with
 * Name, Type, EIN and Owned; State registrations with State, Tax type and State tax ID; each with
 * the Approval column. How a row is reviewed is ListTable's. When neither list has a row, one
 * sentence says so.
 *
 * An EIN is drawn whole and is not masked. An EIN is not a secret and a preparer needs it whole,
 * which is why the api sends it in full.
 */

import type { TaxProfileListRow } from '@shared/types/taxProfile';
import { LIST_EMPTY_SENTENCES } from '@/pages/TaxProfile/wording';
import ListTable, {
  ListTableColumn,
  ListTableProps,
} from '@/pages/TaxProfile/components/ListTable';
import { BRAND_HEADING_CLASS } from '@/pages/TaxProfile/brand';

export const SUBSIDIARIES_SECTION_ID = 'tax-profile-subsidiaries';

export const SUBSIDIARY_COLUMNS: ListTableColumn[] = [
  { key: 'subsidiary_name', header: 'Name' },
  { key: 'subsidiary_entity_type', header: 'Type', widthClass: 'w-[20%]' },
  { key: 'subsidiary_ein', header: 'EIN', widthClass: 'w-[15%]' },
  {
    key: 'subsidiary_ownership_pct',
    header: 'Owned',
    align: 'right',
    widthClass: 'w-[11%]',
  },
];

export const STATE_REGISTRATION_COLUMNS: ListTableColumn[] = [
  { key: 'state_registration_state', header: 'State' },
  {
    key: 'state_registration_tax_type',
    header: 'Tax type',
    widthClass: 'w-[22%]',
  },
  {
    key: 'state_registration_state_tax_id',
    header: 'State tax ID',
    widthClass: 'w-[20%]',
  },
];

export type SubsidiariesTableProps = Omit<
  ListTableProps,
  'rows' | 'columns' | 'label' | 'nameKey' | 'noun' | 'renderFooter'
> & {
  subsidiaries: TaxProfileListRow[];
  stateTaxIds: TaxProfileListRow[];
  /** Added to the section's own classes; TaxProfile.tsx passes the glow of the section in view. */
  className?: string;
};

export default function SubsidiariesTable({
  subsidiaries,
  stateTaxIds,
  className = '',
  ...tableProps
}: SubsidiariesTableProps) {
  const isEmpty = subsidiaries.length === 0 && stateTaxIds.length === 0;

  return (
    <section
      id={SUBSIDIARIES_SECTION_ID}
      className={`mb-6 scroll-mt-4 ${className}`}
    >
      <h3 className={`font-bold mb-1 ${BRAND_HEADING_CLASS}`}>
        Subsidiaries and states
      </h3>
      {isEmpty && (
        <p className='text-xs opacity-70'>
          {LIST_EMPTY_SENTENCES.subsidiariesAndStates}
        </p>
      )}
      {subsidiaries.length > 0 && (
        <div className='mb-3'>
          <h4 className='text-sm font-semibold mb-1'>Subsidiaries</h4>
          <ListTable
            {...tableProps}
            rows={subsidiaries}
            columns={SUBSIDIARY_COLUMNS}
            label='Subsidiaries'
            nameKey='subsidiary_name'
            noun='subsidiary'
          />
        </div>
      )}
      {stateTaxIds.length > 0 && (
        <div>
          <h4 className='text-sm font-semibold mb-1'>State registrations</h4>
          <ListTable
            {...tableProps}
            rows={stateTaxIds}
            columns={STATE_REGISTRATION_COLUMNS}
            label='State registrations'
            nameKey='state_registration_state'
            noun='state registration'
          />
        </div>
      )}
    </section>
  );
}
